import { InputMode } from './FormEnum';
import moment, { isMoment } from 'moment';
import { diff } from 'deep-diff';
import { get } from 'lodash';

import { MonthEnum } from 'Common/Constants/Enumeration/MonthEnum';
import { DATE_FORMAT_FOR_UI, DATE_TIME_FORMAT_FOR_UI } from 'Common/Constants';
import { ClosedStatusEnum } from 'Common/Constants/Enumeration/ClosedStatusEnum';
import MPWWebApi from 'API/MPWApi';

export const defaultDateFormat = date => {
  if (!date) {
    return null;
  }

  if (/\d{1,2}\/\d{1,2}\/\d{4}/.test(date)) {
    return date;
  }

  return moment(date).format(DATE_FORMAT_FOR_UI);
};

export const defaultDateTimeFormat = date => {
  const momentDate = moment(date);

  if (!momentDate.isValid()) return null;

  return momentDate.format(DATE_TIME_FORMAT_FOR_UI);
};

export const getFiscalYear = date => {
  const year = date.getFullYear();
  const startMonth = 6;
  if (date.getMonth() >= startMonth) {
    return year + 1;
  }

  return year;
};

export const getEndOfFiscalYear = fiscalYear => {
  return moment({
    year: fiscalYear,
    month: MonthEnum.June,
    day: 30,
  });
};

export const getStartOfFiscalYear = fiscalYear => {
  return moment({
    year: fiscalYear - 1,
    month: MonthEnum.July,
    day: 1,
  });
};

export const isWeekDay = dateMoment => {
  if (isMoment(dateMoment)) {
    return dateMoment.isoWeekday() < 6;
  }

  throw Error(`Expected a moment but received ${typeof dateMoment}`);
};

export const isOperationalDay = (date, exceptions, format) => {
  if (!format) {
    throw Error('`format` is required for `moment` creation');
  }

  const m = typeof date === 'string' ? moment(date, format) : moment(date);

  if (!exceptions || exceptions.length === 0) {
    return isWeekDay(m);
  }

  const thisDayExceptions = exceptions.filter(
    e =>
      m.isSameOrAfter(moment(e.BeginDay, format)) &&
      m.isSameOrBefore(moment(e.EndDay, format))
  );

  if (thisDayExceptions.find(e => e.IsServing)) {
    return true;
  }

  if (isWeekDay(m) && !thisDayExceptions.find(e => !e.IsServing)) {
    return true;
  }

  return false;
};

export const addOperationalDays = (date, days, exceptions, format) => {
  const m = typeof date === 'string' ? moment(date, format) : moment(date);

  while (days > 0) {
    if (isOperationalDay(m, exceptions, format)) {
      days -= 1;
    }

    m.add(1, 'days');
  }

  while (!isWeekDay(m)) {
    m.add(1, 'days');
  }

  return m.format(format);
};

export const dateIsInFiscalYear = (date, fiscalYear) => {
  const start = moment(new Date(fiscalYear - 1, 6, 1));
  const end = moment(new Date(fiscalYear, 5, 30));
  const dateMoment = moment(date);

  return dateMoment.isSameOrAfter(start) && dateMoment.isSameOrBefore(end);
};

export const getValueFromInputMode = (mode, size, value) => {
  if (mode === InputMode.Decimal && size[1] === 0) {
    const error = {
      message: 'Use InputMode.Integer for 0 decimal places',
    };

    throw error;
  }

  //need to allow an extra character left of the decimal
  //if the negative sign is present
  if (value.indexOf('-') !== -1) {
    size[0] += 1;
  }

  switch (mode) {
    case InputMode.Integer:
      value = value.replace(/[^\d]/g, '');

      if (value.length <= size[0] - (size[1] || 0)) {
        return value;
      }

      return value.substring(0, size[0]);
    case InputMode.Decimal:
      const newValue = value.replace(/[^\d.-]/g, '');

      switch ((newValue.match(/[.]/g) || []).length) {
        case 0:
          //no decimal points
          if (newValue.length <= size[0] - size[1]) {
            return newValue;
          }

          return newValue.substring(0, size[0] - size[1]);

        case 1:
          //1 decimal point
          const parts = newValue.split('.');

          if (parts[0].length > size[0] - size[1]) {
            parts[0] = parts[0].substring(0, parts[0].length - 1);
          }

          if (parts[1].length > size[1]) {
            parts[1] = parts[1].substring(0, parts[1].length - 1);
          }

          return parts.join('.');

        //too many decimal points
        default:
          return value.substring(0, value.length - 1);
      }

    case InputMode.String:
      return value.replace(/[^a-zA-z\s]/g, '');
    case InputMode.Alphanumeric:
      return value.replace(/[^a-z0-9]/gi, '');

    default:
      return value;
  }
};

export const handleModeInputKeyPress = (e, mode, allowNegative, onChange) => {
  const { selectionStart } = e.target;
  let expression = /./;

  switch (mode) {
    case InputMode.Integer:
      expression = allowNegative ? /[\d-]/ : /[\d]/;
      break;
    case InputMode.Decimal:
      expression = allowNegative ? /[\d-.]/ : /[\d.]/;
      break;
    default:
      break;
  }

  if ((e.key.match(expression) || []).length === 0) {
    e.preventDefault();
  } else {
    if (mode === InputMode.Decimal && e.key === '.') {
      if (e.target.value.indexOf('.') !== -1) {
        e.preventDefault();
      }
    }

    if (allowNegative && e.key === '-') {
      e.preventDefault();

      const { value } = e.target;
      const isNegative = value.indexOf('-') !== -1;
      e.target.value = `${isNegative ? '' : '-'}${value.replace(/[-]/g, '')}`;

      e.target.selectionStart = isNegative
        ? selectionStart - 1
        : selectionStart + 1;
      e.target.selectionEnd = e.target.selectionStart;

      //onChange will only be provided from the <TrackedInput />
      //redux-form use will rely on the @@BLUR event to set the negative
      //it is necessary to preventDefault() above to avoid the negative
      //sign being dropped in at the cursor resulting in a potentially
      //invalid number
      if (onChange) {
        onChange(e);
      }
    }
  }
};

export const urlParamObj = () => {
  let search = window.location.search.substring(1);

  if (!search) {
    return {};
  }

  return JSON.parse(
    '{"' +
      decodeURI(search)
        .replace(/"/g, '\\"')
        .replace(/&/g, '","')
        .replace(/=/g, '":"') +
      '"}'
  );
};

export const isNumeric = value => {
  return !isNaN(parseFloat(value)) && isFinite(value);
};

export const delay = ms => new Promise(resolve => setTimeout(resolve, ms));
export const submitDelay = (value = 1) => {
  return delay(value);
};

export const getIsModified = (
  original,
  modified,
  ignoreEmptyStringToNull = true
) => {
  const changes = diff(original, modified);

  if (changes === undefined) {
    return false;
  }

  if (!ignoreEmptyStringToNull) {
    return changes.length > 0;
  }

  return (
    changes.filter(change => {
      const { lhs, rhs } = change;
      return !(
        (lhs === '' && rhs === null) ||
        //Need to exclude if the difference is No change checked
        (
          get(change, 'path[1]') === 'SecondReviewResultId' &&
          lhs === null &&
          rhs === 1
        ) || (
          get(change, 'path[0]') === 'Students' &&
          get(change, 'path[2]') === 'EffectiveDate'
        ) || (
          get(change, 'path[0]') === 'Students' &&
          get(change, 'path[2]') === 'StatusChangeType'
        )
      );
    }).length > 0
  );
};

/**
 * Checks if a day is closed for B/L/O or Supper
 * @param  {Object} dayClosedModel
 * @param {String} dayClosedModel.PrimarySiteId
 * @param {String} dayClosedModel.ClosingDate
 * @param {Boolean} [dayClosedModel.Supper]
 * @returns {Boolean}
 */
export const getIsDayClosed = async dayClosedModel => {
  try {
    const ClosedStatusResponse = await MPWWebApi.postGenericInsertUpdate(
      '/Serving/GetClosedStatus',
      dayClosedModel
    );

    const closedStatus = ClosedStatusResponse.data.Data;
    if (closedStatus && closedStatus.length > 0) {
      return closedStatus[0].ClosedStatus === ClosedStatusEnum.CLOSED;
    } else {
      return false;
    }
  } catch (error) {
    throw error;
  }
};

/**
 * Create a callback to be called by a keyEvent listener (onKeyDown, onKeyUp)
 * @param {object} listeners - An object of callbacks. Each callback should
 *   return a bool specifying whether event.preventDefault() should be called.
 * @param {function} [listeners.onTab] - Called on tab (NOT on shift+tab)
 * @param {function} [listeners.onEnter] - Called on enter
 */
export const createKeyEventListener = listeners => {
  return e => {
    let prevent = false;

    if (e.key === 'Enter') {
      prevent = listeners.onEnter && listeners.onEnter(e);
    } else if (e.key === 'Tab' && !e.shiftKey) {
      prevent = listeners.onTab && listeners.onTab(e);
    }

    if (prevent) {
      e.preventDefault();
    }
  };
};



// WEBPACK FOOTER //
// ./src/common/GeneralHelper.js